import React, { createContext, useContext, useEffect, useState } from 'react';
import { fetchWithSessionToken } from '../components/shared/fetchWithSessionToken';
import { CHAT_API_BASE } from '../config/api';

export const defaultTheme = {
  imageSrc: 'https://d1f6rj4sne1926.cloudfront.net/brella_logo.png',
  backgroundImage: `url("data:image/svg+xml,%3Csvg width='100' height='100' xmlns='http://www.w3.org/2000/svg'%3E%3Ctext x='0' y='75' font-size='75' fill='%23865FFE' opacity='0.08'%3E☂%3C/text%3E%3C/svg%3E")`,
  faviconKey: '',
  backgroundColor: '#F1EDFF',
  accentColor: '#8661FF',
  textDark: '#12273F',
  textLight: '#6E84A3',
  borderColor: '#DADEE5',
  buttonTextColor: '#FFFFFF',
  headingFont: 'Inter, sans-serif',
  bodyFont: 'Inter, sans-serif',
  store: 'test-brella-ai.myshopify.com',
  returnPolicy: '',
  // New flow-specific content settings
  flowPortalContentSettings: {
    RETURN: {
      orderLookup: {
        returnPolicyText: 'Returns & Exchanges Policy',
        returnPolicyUrl: 'https://yourstore.com/policies/returns',
        headingText: 'Returns & Exchanges',
        subheadingText: "First, let's look up your order.",
        requiredFields: [],
        giftReturnsEnabled: false,
        giftText: '',
        giftLinkText: '',
        additionalInfoPlaceholder: 'Email or Zip or Phone #',
        orderNumberPlaceholder: 'Order # (emailed to you)'
      },
      itemSelection: {
        title: 'Select Items to Return',
        description: 'Choose the items you want to return or exchange',
        headingText: 'Select an item to return',
        subheadingText: 'Your return or exchange is free!',
        quantityLabel: 'Quantity to Return',
        availableLabel: 'Available',
        alreadyInReturnText: 'Already in return',
        errorMessage: 'Please select at least one item to continue.'
      },
      review: {
        title: 'Review your return',
        headingTextReturning: "What you're returning",
        bodyTextReturning: 'Your card will be credited once package is received in returnable condition.',
        headingTextReceiving: "What you'll receive",
        bodyTextReceiving: 'Your exchange will ship once return is received in returnable condition.',
        summaryTitle: 'Return Summary',
        itemsBeingReturnedText: 'Items being returned',
        discountsAppliedText: 'Discounts applied',
        estimatedReturnAmountText: 'Estimated return amount',
        finalAmountNote: 'Final return amount will be determined after review.',
        submitReturnText: 'Submit Return',
        submittingReturnText: 'Submitting Return...',
        disabledDuringDemoText: 'Disabled during demo',
        shippingInformationText: 'Shipping information',
        shippingAddressText: 'Shipping Address',
        billingAddressText: 'Billing Address',
        returnReasonLabel: 'Return Reason',
        cancelText: 'Cancel',
        closeText: 'Close'
      }
    },
    CLAIM: {
      orderLookup: {
        returnPolicyText: 'Claims & Protection Policy',
        returnPolicyUrl: 'https://yourstore.com/policies/claims',
        headingText: 'File a Claim',
        subheadingText: "Let's look up your order to file a claim.",
        requiredFields: [],
        giftReturnsEnabled: false,
        giftText: '',
        giftLinkText: '',
        additionalInfoPlaceholder: 'Email or Zip or Phone #',
        orderNumberPlaceholder: 'Order # (emailed to you)'
      },
      itemSelection: {
        title: 'Select Item(s) to Claim',
        description: 'Choose the items you want to include in your claim',
        headingText: 'Select items to claim',
        subheadingText: "We'll help you resolve any issues with your order.",
        claimReasonLabel: 'Claim Reason',
        quantityLabel: 'Quantity to Claim',
        availableLabel: 'Available',
        alreadyInClaimText: 'Already in claim',
        errorMessage: 'Please select at least one item to continue.'
      },
      workflowSelection: {
        issueTitle: 'Select Claim Reason',
        issueSubtitle: 'Choose the reason for your claim',
        resolutionTitle: 'What would you like to do?',
        resolutionSubtitle: 'Select your preferred resolution for each item'
      },
      review: {
        title: 'Review your claim',
        headingTextReturning: 'Items in your claim',
        bodyTextReturning: "We'll review your claim and get back to you within 24-48 hours.",
        headingTextReceiving: "What you'll receive",
        bodyTextReceiving: 'Based on your claim, we may provide a refund, replacement, or store credit.',
        summaryTitle: 'Claim Summary',
        itemsBeingClaimedText: 'Items being claimed',
        discountsAppliedText: 'Discounts applied',
        estimatedClaimAmountText: 'Estimated claim amount',
        finalAmountNote: 'Final claim amount will be determined after review.',
        submitClaimText: 'Submit Claim',
        submittingClaimText: 'Submitting Claim...',
        disabledDuringDemoText: 'Disabled during demo',
        shippingInformationText: 'Shipping information',
        shippingAddressText: 'Shipping Address',
        billingAddressText: 'Billing Address',
        issueLabel: 'Issue',
        resolutionLabel: 'Resolution',
        cancelText: 'Cancel',
        closeText: 'Close'
      },
      submitted: {
        title: 'Claim Submitted Successfully!',
        message: "Thank you for submitting your claim. We've received your request and will review it within 24-48 hours.",
        nextStepsTitle: 'What happens next?',
        nextSteps: [
          'Our team will review your claim within 24-48 hours',
          "You'll receive an email confirmation with your claim details",
          "We'll contact you if we need any additional information",
          'Once approved, we\'ll process your refund or replacement'
        ],
        backButtonText: 'Back to Order'
      }
    }
  }
};

const PortalThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(PortalThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a PortalThemeProvider');
  }
  return context.theme;
};

export const useSetShopId = () => {
  const context = useContext(PortalThemeContext);
  if (!context) {
    throw new Error('useSetShopId must be used within a PortalThemeProvider');
  }
  return context.setShopId;
};

// New hook to get flow-specific portal content settings
export const useFlowPortalContent = (flowType = 'RETURN') => {
  const context = useContext(PortalThemeContext);
  if (!context) {
    throw new Error('useFlowPortalContent must be used within a PortalThemeProvider');
  }
  
  const theme = context.theme;
  
  // Try to get flow-specific settings first
  if (theme.flowPortalContentSettings && theme.flowPortalContentSettings[flowType]) {
    return theme.flowPortalContentSettings[flowType];
  }
  
  // Fallback to default theme settings if theme doesn't have flow-specific settings
  if (defaultTheme.flowPortalContentSettings && defaultTheme.flowPortalContentSettings[flowType]) {
    return defaultTheme.flowPortalContentSettings[flowType];
  }
  
  // Final fallback to empty object to prevent errors
  return {};
};

export const PortalThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState(defaultTheme);
  const [shopId, setShopId] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
    const loadTheme = async () => {
      try {
        // The ?shop= param (exposed as __SHOPIFY_CONFIG__.shop) is the
        // authoritative store for this page load: it's injected by the App
        // Proxy for live shoppers and by the merchant-admin preview iframe
        // (?shop=<currentShop>). It MUST win over localStorage.shopId, which is
        // a sticky leftover that any prior order lookup writes on this shared
        // brellaclaims.com origin. Reading localStorage first made the preview
        // — and any shopper who'd used a different store's portal — load the
        // wrong store's theme, falling back to the Brella default. Matches the
        // precedence already used in LandingPage.jsx.
        const storedShopId = window.__SHOPIFY_CONFIG__?.shop || localStorage.getItem('shopId') || 'test-brella-ai.myshopify.com';
        setShopId(storedShopId);

        const response = await fetchWithSessionToken(
          `${CHAT_API_BASE}/shopify/shop-theme?shop=${storedShopId}`
        );

        if (response.ok) {
          const themeData = await response.json();
          setTheme(prevTheme => ({
            ...prevTheme,
            ...themeData,
            store: storedShopId
          }));
        } else {
          console.warn(`No theme found for ${storedShopId}. Using default.`);
        }
      } catch (error) {
        console.error('Error loading theme:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadTheme();
  }, [shopId]);

  // Inject CSS variables
  useEffect(() => {
    if (!isLoading) {
      const root = document.documentElement;
      const setCSS = (key, value) => root.style.setProperty(`--${key}`, value);

      setCSS('button-text-color', theme.buttonTextColor);
      setCSS('background-color', theme.backgroundColor);
      setCSS('text-dark', theme.textDark);
      setCSS('text-light', theme.textLight);
      setCSS('accent-color', theme.accentColor);
      setCSS('border-color', theme.borderColor);
      setCSS('background-image', theme.backgroundImage || '');
      setCSS('heading-font', theme.headingFont);
      setCSS('body-font', theme.bodyFont);

      const updateFavicon = () => {
        if (!theme.faviconKey) return;

        let favicon = document.querySelector("link[rel~='icon']");
        if (!favicon) {
          favicon = document.createElement('link');
          favicon.rel = 'icon';
          document.head.appendChild(favicon);
        }
        favicon.href = theme.faviconKey;
      };

      updateFavicon();
    }
  }, [theme, isLoading]);

  if (isLoading) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ fontSize: '16px', color: 'var(--disabled-text-dark' }}>Loading...</div>
      </div>
    );
  }

  return (
    <PortalThemeContext.Provider value={{ theme, setShopId }}>
      {children}
    </PortalThemeContext.Provider>
  );
};
